"use client";
import { API_URL } from "@/lib/config";

// SocialBar — floating like button + comment toggle for PanoramaViewer.
// Renders as a bottom-left pill overlay inside the viewer.

import { useEffect, useState } from "react";
import { Heart, MessageCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/features/auth";

const API = API_URL;

interface SocialBarProps {
  panoramaId: string;
  initialLikeCount?: number;
  initialCommentCount?: number;
  onOpenComments: () => void;
}

export default function SocialBar({
  panoramaId,
  initialLikeCount = 0,
  initialCommentCount = 0,
  onOpenComments,
}: SocialBarProps) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [toggling, setToggling] = useState(false);

  // Fetch current like status on mount (best-effort)
  useEffect(() => {
    fetch(`${API}/api/panoramas/${panoramaId}/like`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!data) return;
        setLiked(data.liked ?? false);
        setLikeCount(data.like_count ?? initialLikeCount);
      })
      .catch(() => {});
  }, [panoramaId, initialLikeCount]);

  const toggleLike = async () => {
    if (!user || toggling) return;
    setToggling(true);
    // Optimistic update
    const nextLiked = !liked;
    setLiked(nextLiked);
    setLikeCount((c) => c + (nextLiked ? 1 : -1));
    try {
      const res = await fetch(`${API}/api/panoramas/${panoramaId}/like`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setLiked(data.liked);
        setLikeCount(data.like_count);
      } else {
        // Revert
        setLiked(!nextLiked);
        setLikeCount((c) => c + (nextLiked ? -1 : 1));
      }
    } catch {
      setLiked(!nextLiked);
      setLikeCount((c) => c + (nextLiked ? -1 : 1));
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="flex items-center gap-1 bg-canvas/40 backdrop-blur-xl border border-ink/20 rounded-2xl shadow-2xl overflow-hidden px-1 py-1">
      {/* Like */}
      <button
        onClick={toggleLike}
        disabled={!user || toggling}
        title={user ? (liked ? "Unlike" : "Like") : "Sign in to like"}
        className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl transition-all ${
          liked
            ? "text-pink-400 bg-pink-500/15"
            : "text-ink/70 hover:text-ink hover:bg-ink/10"
        } disabled:cursor-default`}
      >
        {toggling ? (
          <Loader2 className="w-4.5 h-4.5 animate-spin" />
        ) : (
          <Heart className={`w-4.5 h-4.5 ${liked ? "fill-pink-400" : ""}`} />
        )}
        {likeCount > 0 && (
          <span className="text-xs font-semibold tabular-nums">{likeCount.toLocaleString()}</span>
        )}
      </button>

      <div className="w-px h-6 bg-ink/10" />

      {/* Comments */}
      <button
        onClick={onOpenComments}
        title="View comments"
        className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-ink/70 hover:text-ink hover:bg-ink/10 transition-all"
      >
        <div className="relative">
          <MessageCircle className="w-4.5 h-4.5" />
          {initialCommentCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-blue-500 text-[9px] font-bold text-white flex items-center justify-center leading-none">
              {initialCommentCount > 99 ? "99+" : initialCommentCount}
            </span>
          )}
        </div>
      </button>
    </div>
  );
}
